import { CaptchaAction } from './captcha'
import { createTokenMinter, TokenMinter } from './recaptchaEnterprise'

export const CAPTCHA_TOKEN_HEADER = 'recaptcha-token'

// Short, because the request waits on it: a blocked reCAPTCHA host must not stall sign-in.
const SCRIPT_LOAD_TIMEOUT_MS = 3_000

export type Captcha = {
  /** Mints a token per call, so a caller mints at submit. Answers no header instead of throwing. */
  getHeaders: (action: CaptchaAction) => Promise<Record<string, string>>
  /** Shows Google's badge, which is the required notice, and returns the call that hides it again. */
  showBadge: (action: CaptchaAction) => () => void
}

/**
 * A site key resolving to nothing turns both minting and the badge off, so a caller gates the whole
 * thing in one place. Only the API decides what a missing token means.
 */
export const createCaptcha = (
  // Read at call time, so importing a login service never depends on config being ready.
  getSiteKey: () => string | undefined,
  scriptLoadTimeoutMs = SCRIPT_LOAD_TIMEOUT_MS
): Captcha => {
  let host: HTMLElement | undefined
  let minter: TokenMinter | undefined
  let badgesShown = 0

  // Another widget on the page loaded the non-Enterprise `api.js`, which shares `window.grecaptcha`.
  // Loading `enterprise.js` over it could break that widget.
  const usableSiteKey = () => {
    const siteKey = getSiteKey()

    if (!siteKey || (window.grecaptcha && !window.grecaptcha.enterprise))
      return undefined

    return siteKey
  }

  const getMinter = (siteKey: string) => {
    if (!host || !minter) {
      host = document.createElement('div')
      // The widget outlives the login form, so the badge is shown only while a page asks for it.
      host.style.visibility = badgesShown > 0 ? 'visible' : 'hidden'
      document.body.appendChild(host)

      minter = createTokenMinter(siteKey, host, scriptLoadTimeoutMs)
    }

    return minter
  }

  const setBadgeVisible = () => {
    if (host) host.style.visibility = badgesShown > 0 ? 'visible' : 'hidden'
  }

  return {
    getHeaders: async (action): Promise<Record<string, string>> => {
      const siteKey = usableSiteKey()

      if (!siteKey) return {}

      try {
        const token = await getMinter(siteKey).getToken(action)

        return token ? { [CAPTCHA_TOKEN_HEADER]: token } : {}
      } catch {
        // `grecaptcha.render` throws outright on a misconfigured key.
        return {}
      } finally {
        // Tokens are single use. Drop the spent one now, or the next challenge returns it.
        minter?.reset()
      }
    },
    showBadge: (action) => {
      const siteKey = usableSiteKey()

      if (!siteKey) return () => undefined

      badgesShown += 1
      getMinter(siteKey)
        .prepare(action)
        .catch(() => undefined)
      setBadgeVisible()

      let hidden = false

      return () => {
        if (hidden) return

        hidden = true
        badgesShown -= 1
        setBadgeVisible()
      }
    },
  }
}
